/** Stage 5: how a row finds its record, and the safety guards. */

import { Checkbox, Field, FieldRow, GuardList, Input, Select } from "@/components/kit";
import { getIntegration, listIntegrations, type FieldsInfo } from "@/lib/integrations/api";
import { normaliseSettings, type Settings } from "@/lib/integrations/settings";

import { useKeyed } from "../hooks";

const MATCH_WORDS: Record<string, string> = {
  externalId: "Their id, through id links",
  noc: "Country code",
  name: "Name",
  key: "Key",
};

export function MatchStage({
  code,
  eventCode,
  settings,
  onChange,
  fields,
  disabled = false,
}: {
  code: string;
  /** The event, to offer the id sources its other integrations already use. */
  eventCode?: string;
  settings: Settings;
  onChange: (settings: Settings) => void;
  fields: FieldsInfo;
  disabled?: boolean;
}) {
  const sources = useKeyed(eventCode ? `id-sources|${eventCode}|${code}` : null, () =>
    listIntegrations(eventCode)
      .then((rows) =>
        Promise.all(
          rows
            .filter((row) => row.code !== code)
            .map((row) =>
              getIntegration(row.code).then(
                (one) => normaliseSettings(one.live?.settings ?? one.draft?.settings ?? {}).match.idSource ?? one.code,
                () => null,
              ),
            ),
        ),
      )
      .then((found) => [...new Set(found.filter((one): one is string => Boolean(one)))]),
  );
  const match = settings.match;
  const set = (patch: Partial<Settings["match"]>) => onChange({ ...settings, match: { ...match, ...patch } });
  const keyable = fields.fields.filter((field) => !field.many);
  return (
    <div style={{ display: "grid", gap: "var(--s5)" }}>
      <div style={{ display: "grid", gap: "var(--s3)" }}>
        <strong>Match</strong>
        <FieldRow>
          <Field label="Whose ids the rows carry" hint={`Id links under this name find our records. Empty uses ${code}.`}>
            <Input
              value={match.idSource ?? ""}
              placeholder={code}
              disabled={disabled}
              onChange={(event) => set({ idSource: event.target.value.trim() || null })}
            />
          </Field>

          {fields.matchOptions.length > 0 && (
            <Field label="Find a record by">
              <Select value={match.matchBy} disabled={disabled} onChange={(event) => set({ matchBy: event.target.value })}>
                {fields.matchOptions.map((option) => (
                  <option key={option} value={option}>
                    {MATCH_WORDS[option] ?? option}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </FieldRow>
        {(sources.data ?? []).length > 0 && (
          <p style={{ margin: 0, fontSize: "var(--t-sm)", color: "var(--ink-2)", display: "flex", flexWrap: "wrap", gap: "var(--s2)", alignItems: "center" }}>
            Other integrations of this event read ids from:
            {(sources.data ?? []).map((source) => (
              <button
                key={source}
                type="button"
                disabled={disabled || match.idSource === source}
                onClick={() => set({ idSource: source })}
                style={{ border: "1px solid var(--line-2)", borderRadius: "var(--r-full)", background: match.idSource === source ? "var(--accent-soft)" : "var(--surface)", padding: "2px var(--s2)", cursor: "pointer", font: "inherit" }}
                aria-pressed={match.idSource === source}
              >
                {source}
              </button>
            ))}
          </p>
        )}
        <div>
          <div style={{ fontSize: "var(--t-sm)", color: "var(--ink-2)", marginBottom: "var(--s2)" }}>
            A row is the same thing across runs when these match. “Only changes” and “newest wins” compare by them.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--s2) var(--s4)" }}>
            {keyable.map((field) => (
              <Checkbox
                key={field.key}
                label={field.label}
                disabled={disabled}
                checked={match.keyFields.includes(field.key)}
                onChange={(event) =>
                  set({
                    keyFields: event.target.checked
                      ? [...match.keyFields, field.key]
                      : match.keyFields.filter((key) => key !== field.key),
                  })
                }
              />
            ))}
          </div>
          {match.keyFields.length === 0 && (
            <p style={{ color: "var(--warn)", margin: "var(--s2) 0 0" }}>Pick at least one field, or every run sends every row again.</p>
          )}
        </div>
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>
          When two records fit, the row is always held for a person. It shows in Needs answers.
        </p>
      </div>
      <div style={{ display: "grid", gap: "var(--s3)" }}>
        <strong>Safety</strong>
        <GuardList safety={settings.safety} disabled={disabled} onChange={(safety) => onChange({ ...settings, safety })} />
      </div>
    </div>
  );
}
